import { useState, useMemo } from 'react';
import { safeFormat } from '@/lib/safeDate';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, ArrowLeftRight, RefreshCw, Edit, Trash2 } from 'lucide-react';
import { FinancePageLayout } from '@/components/finance';
import { useExchangeRates, convertCurrency, type ExchangeRate } from '@/hooks/finance/useExchangeRates';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

export default function ExchangeRates() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [convertFrom, setConvertFrom] = useState('USD');
  const [convertTo, setConvertTo] = useState('BRL');
  const [convertAmount, setConvertAmount] = useState('1000');
  
  const { data: rates, isLoading } = useExchangeRates();

  // Get latest rates for each currency pair
  const latestRates = useMemo(() => {
    if (!rates) return [];
    const latest: Record<string, ExchangeRate> = {};
    rates.forEach(r => {
      const key = `${r.from_currency}-${r.to_currency}`;
      if (!latest[key]) {
        latest[key] = r;
      }
    });
    return Object.values(latest);
  }, [rates]);

  const usdToBrl = latestRates.find(r => r.from_currency === 'USD' && r.to_currency === 'BRL');
  const brlToUsd = latestRates.find(r => r.from_currency === 'BRL' && r.to_currency === 'USD');

  // Get the rate for conversion
  const currentRate = useMemo(() => {
    const rate = latestRates.find(r => r.from_currency === convertFrom && r.to_currency === convertTo);
    return rate?.rate || 1;
  }, [latestRates, convertFrom, convertTo]);

  const convertedAmount = convertCurrency(
    Number(convertAmount) || 0,
    convertFrom,
    convertTo,
    currentRate
  );

  const formatDate = (dateStr: string) => {
    return safeFormat(new Date(dateStr), 'dd/MM/yyyy HH:mm', { locale: ptBR });
  };

  return (
    <FinancePageLayout>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Taxas de Câmbio</h1>

        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Nova Taxa
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Adicionar Taxa de Câmbio</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>De</Label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Moeda origem" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD - Dólar Americano</SelectItem>
                      <SelectItem value="BRL">BRL - Real Brasileiro</SelectItem>
                      <SelectItem value="EUR">EUR - Euro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Para</Label>
                  <Select>
                    <SelectTrigger>
                      <SelectValue placeholder="Moeda destino" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD - Dólar Americano</SelectItem>
                      <SelectItem value="BRL">BRL - Real Brasileiro</SelectItem>
                      <SelectItem value="EUR">EUR - Euro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Taxa de Conversão</Label>
                <Input type="number" step="0.0001" placeholder="5.1234" />
              </div>
              <div className="space-y-2">
                <Label>Data de Vigência</Label>
                <Input type="date" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={() => setIsDialogOpen(false)}>
                  Salvar
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Current Rates Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">USD → BRL</CardTitle>
            <ArrowLeftRight className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {usdToBrl ? `R$ ${usdToBrl.rate.toFixed(4)}` : '-'}
            </div>
            {usdToBrl && (
              <p className="text-xs text-muted-foreground">
                Atualizado em {formatDate(usdToBrl.effective_date)}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">BRL → USD</CardTitle>
            <ArrowLeftRight className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {brlToUsd ? `$ ${brlToUsd.rate.toFixed(4)}` : '-'}
            </div>
            {brlToUsd && (
              <p className="text-xs text-muted-foreground">
                Atualizado em {formatDate(brlToUsd.effective_date)}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Converter */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Conversor</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={convertAmount}
                onChange={(e) => setConvertAmount(e.target.value)}
                className="w-24"
              />
              <Select value={convertFrom} onValueChange={setConvertFrom}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="BRL">BRL</SelectItem>
                </SelectContent>
              </Select>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => {
                  setConvertFrom(convertTo);
                  setConvertTo(convertFrom);
                }}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Select value={convertTo} onValueChange={setConvertTo}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="BRL">BRL</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-lg font-bold text-center">
              {convertTo === 'BRL' ? 'R$' : '$'} {convertedAmount.toFixed(2)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Historical Rates Table */}
      <Card>
        <CardHeader>
          <CardTitle>Histórico de Taxas</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>De</TableHead>
                  <TableHead>Para</TableHead>
                  <TableHead className="text-right">Taxa</TableHead>
                  <TableHead>Data de Vigência</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rates?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                      Nenhuma taxa cadastrada
                    </TableCell>
                  </TableRow>
                ) : (
                  rates?.map((rate) => {
                    const isLatest = latestRates.some(
                      lr => lr.from_currency === rate.from_currency && 
                            lr.to_currency === rate.to_currency && 
                            lr.id === rate.id
                    );
                    
                    return (
                      <TableRow key={rate.id}>
                        <TableCell>
                          <Badge variant="outline">{rate.from_currency}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{rate.to_currency}</Badge>
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {rate.rate.toFixed(4)}
                        </TableCell>
                        <TableCell>{formatDate(rate.effective_date)}</TableCell>
                        <TableCell>
                          {isLatest ? (
                            <Badge className="bg-green-100 text-green-800">Atual</Badge>
                          ) : (
                            <Badge variant="secondary">Histórico</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="icon">
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="icon">
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </FinancePageLayout>
  );
}
